import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { useCreateTagMutation, useDeleteTagMutation, useTagsQuery } from '@platform/state/api';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { useWorkspaceMeta } from '../../shell/useWorkspaceMeta';
import { useFailToast } from '../system/useFailToast';
import './customize.css';

/** Відтінки кольору нового тега (прототип v4 HUES) і колір позначки oklch(0.66 0.14 h). */
const HUES = [27, 75, 150, 200, 255, 300];
const dot = (hue: number) => `oklch(0.66 0.14 ${hue})`;

/**
 * «Теги» (прототип v4 G:L10188–10240, pgTags): «Спільні для всіх модулів у просторі …», поле «Назва нового тегу», шість кольорів і
 * «Додати тег» (POST /workspaces/{id}/tags; Enter теж додає), перелік тегів з кольором, кількістю записів і «Видалити» (DELETE …/tags/{id}:
 * тег зникає з усіх записів простору, після підтвердження); «Тегів ще немає.». Кнопка активна й без назви (як у прототипі; порожню
 * назву не додає); блок 760px з відступами 28px, як у прототипі.
 */
export function CustTagsPage() {
  const { t } = useTranslation();
  const { ws } = useWorkspaceParams();
  const { workspace } = useWorkspaceMeta(ws);
  const { data: tags } = useTagsQuery(ws, { skip: !ws });
  const [createTag, { isLoading }] = useCreateTagMutation();
  const [deleteTag] = useDeleteTagMutation();
  const fail = useFailToast();
  const [name, setName] = useState('');
  const [hue, setHue] = useState(HUES[4]!);
  const [error, setError] = useState('');
  const clean = name.trim().replace(/\s+/g, ' ');
  const add = () => {
    if (!clean || isLoading) return;
    if (tags?.some((x) => x.name.toLocaleLowerCase() === clean.toLocaleLowerCase())) { setError(t('sysCust.tags.exists')); return; }
    setError('');
    void createTag({ ws, body: { name: clean, hue } }).unwrap().then(() => setName('')).catch(() => setError(t('sysCust.tags.failed')));
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); add(); } };
  return (
    <div className="cu-tags">
      <div className="sp-head__titles">
        <h1 className="cu-tags__title">{t('sysPages.sections.tags')}</h1>
        <div className="sp-sub">{t('sysCust.tags.sub', { ws: workspace?.name ?? '' })}</div>
      </div>
      <div className="cu-tags__add">
        <input className="sp-input cu-tags__input" value={name} placeholder={t('sysCust.tags.newName')} aria-label={t('sysCust.tags.newName')}
          onChange={(e) => setName(e.target.value)} onKeyDown={onKey} />
        <div className="cu-tags__hues" role="radiogroup" aria-label={t('sysCust.tags.colour')}>
          {HUES.map((h) => <button key={h} type="button" role="radio" aria-checked={h === hue} aria-label={t('sysCust.tags.colour')}
            className={cx('cu-tags__hue', h === hue && 'is-on')} style={{ background: dot(h) }} onClick={() => setHue(h)} />)}
        </div>
        <button type="button" className="sp-btn sp-btn--primary cu-tags__btn" disabled={isLoading} onClick={add}>{t('sysCust.tags.add')}</button>
        {error && <span className="cu-tags__error" role="alert">{error}</span>}
      </div>
      <div className="cu-tags__list">
        {(tags ?? []).map((tag) => (
          <div key={tag.id} className="cu-tags__row">
            <span className="cu-tags__dot" style={{ background: dot(tag.hue) }} aria-hidden="true" />
            <span className="cu-tags__name">{tag.name}</span>
            <span className="sp-count">{t('sysCust.tags.records', { count: tag.usageCount })}</span>
            <button type="button" className="cu-tags__del" onClick={() => { if (window.confirm(t('sysCust.tags.deleteConfirm', { name: tag.name }))) deleteTag({ ws, tagId: tag.id }).unwrap().catch(fail); }}>
              {t('sysCust.tags.delete')}
            </button>
          </div>
        ))}
        {tags && !tags.length && <div className="sp-empty">{t('sysCust.tags.none')}</div>}
      </div>
    </div>
  );
}
